/**
 * Tier B missions (Earth orbit, Concept §3.4; Sprint 3, S3-21) have no ephemeris: they fly a symbolic Kepler
 * ellipse around the Earth with their real perigee, apogee, inclination and period. The orbit's orientation in
 * space is not historical (node and perigee direction are fixed), only its shape, tilt and timing are. Pure.
 */
import { type Vec3, set } from './vec3';

export interface SymbolicOrbit {
  /** Perigee and apogee above the surface, km. */
  perigee: number;
  apogee: number;
  /** Inclination to the equator, degrees. */
  inclination: number;
  /** Period, minutes. */
  period: number;
}

/** Earth's obliquity: the equator's tilt against the ecliptic (J2000), radians. */
const OBLIQUITY = (23.4393 * Math.PI) / 180;

/**
 * Position relative to the Earth's centre (km, ecliptic J2000 axes) `days` after launch, written into `out`. The
 * orbit starts at perigee at launch.
 */
export function symbolicOrbitOffset(o: SymbolicOrbit, earthRadius: number, days: number, out: Vec3): Vec3 {
  const rp = earthRadius + o.perigee;
  const ra = earthRadius + o.apogee;
  const a = (rp + ra) / 2;
  const e = (ra - rp) / (ra + rp);
  const n = (2 * Math.PI) / (o.period / 1440);
  const M = (n * days) % (2 * Math.PI);
  // Kepler's equation by Newton's method (e < 0.1 for every Earth orbit here)
  let E = M;
  for (let k = 0; k < 6; k++) E -= (E - e * Math.sin(E) - M) / (1 - e * Math.cos(E));
  const x = a * (Math.cos(E) - e);
  const y = a * Math.sqrt(1 - e * e) * Math.sin(E);
  // tilted by the inclination against the equator, which is tilted by the obliquity against the ecliptic
  const tilt = (o.inclination * Math.PI) / 180 + OBLIQUITY;
  return set(out, x, y * Math.cos(tilt), y * Math.sin(tilt));
}
